// src/utils/uploadHelper.js
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<Blob>} - Returns a promise that resolves to the compressed image
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    type = file.type || 'image/jpeg'
  } = options;
  
  return new Promise((resolve, reject) => {
    // Create an image object
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw the image on the canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          console.log(`Image compressed: ${file.size / 1024} KB -> ${blob.size / 1024} KB`);
          resolve(blob);
        } else {
          reject(new Error('Failed to compress image'));
        }
      }, type, quality);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };
  });
};

/**
 * Retries a function multiple times with exponential backoff
 * @param {Function} fn - The function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise} - The result of the function
 */
const retry = async (fn, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      console.log(`Attempt ${i + 1} failed, retrying...`, error);
      lastError = error;
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
    }
  }
  throw lastError;
};

/**
 * Helper function to safely upload a file to Firebase Storage with compression
 * @param {File} file - The file object to upload
 * @param {string} path - The storage path where the file should be stored
 * @param {function} onProgress - Optional callback for upload progress
 * @param {number} timeoutMs - Timeout in milliseconds (default: 60000)
 * @returns {Promise<string>} - Returns a promise that resolves to the download URL
 */
export const uploadFile = async (file, path, onProgress = null, timeoutMs = 60000) => {
  if (!file) {
    console.warn("No file provided for upload");
    return null;
  }
  
  try {
    console.log(`Starting upload for file: ${file.name} (${file.size} bytes)`);
    
    // Check if it's an image that can be compressed
    let fileToUpload = file;
    // Compress images larger than 100KB, aiming for 100-200KB output
    if (file.type.startsWith('image/') && file.size > 100 * 1024) { 
      try {
        console.log('Compressing image before upload...');
        fileToUpload = await compressImage(file, {
          maxWidth: 1200, 
          maxHeight: 1200,
          quality: 0.6 // Lowered quality to reduce file size further
        });
        console.log(`Compression complete: ${file.size} -> ${fileToUpload.size} bytes (${Math.round(fileToUpload.size / file.size * 100)}%)`);
      } catch (compressError) {
        console.warn('Image compression failed, using original file:', compressError);
        fileToUpload = file;
      }
    }
    
    // Get file extension and create a unique filename
    const fileExtension = file.name.split('.').pop();
    const uniqueFileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;
    const fullPath = `${path}/${uniqueFileName}`;
    
    console.log(`Upload path: ${fullPath}`);
    
    // Set up metadata
    const metadata = {
      contentType: file.type || 'application/octet-stream', // Default if type is missing
      cacheControl: 'public,max-age=31536000' // Cache for 1 year
    };
    
    // Use Promise to handle the upload with timeout
    // return await retry(async () => { // REMOVED: Custom retry wrapper
      return new Promise((resolve, reject) => {
        // Create storage reference
        const storageRef = ref(storage, fullPath);
        
        // Start the upload
        const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata);
        
        // Track upload state
        let lastProgressUpdate = Date.now();
        let uploadStartTime = Date.now();
        
        // Set a timeout
        /* REMOVED: Custom timeout logic
        const timeoutId = setTimeout(() => {
          // If we hit the timeout, cancel the upload and reject
          console.error(`Upload timed out after ${timeoutMs}ms`);
          reject(new Error(`Upload timed out after ${timeoutMs}ms`));
        }, timeoutMs);
        */
        
        // Listen for state changes, errors, and completion
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Get upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            
            // Limit progress updates to once per second to avoid UI jank
            const now = Date.now();
            if (now - lastProgressUpdate > 1000) {
              console.log(`Upload progress: ${progress.toFixed(1)}%`);
              lastProgressUpdate = now;
              
              // Call progress callback if provided
              if (onProgress) {
                onProgress(progress);
              }
            }
          },
          (error) => {
            // Handle unsuccessful uploads
            console.error("Upload failed:", error);
            reject(error);
          },
          async () => {
            // Handle successful upload
            console.log("Upload successful");
            
            // Clear the timeout since we've succeeded
            // clearTimeout(timeoutId); // REMOVED: No longer needed
            
            try {
              // Get the download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("File download URL:", downloadURL);
              resolve(downloadURL);
            } catch (urlError) {
              console.error("Error getting download URL:", urlError);
              reject(urlError);
            }
          }
        );
      });
    // }, 2); // REMOVED: Custom retry wrapper
  } catch (error) {
    console.error("Error in uploadFile helper:", error);
    // Log the full error object for more details
    console.error("Full error object:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
    throw error;
  }
};

/**
 * Validate a file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {boolean|string} - Returns true if valid, or error message if invalid
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSizeMB = 5,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  } = options;
  
  if (!file) {
    return "No file provided";
  }
  
  // Check file size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return `File size (${fileSizeMB.toFixed(2)}MB) exceeds maximum allowed (${maxSizeMB}MB)`;
  }
  
  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return `File type (${file.type}) not allowed. Allowed types: ${allowedTypes.join(', ')}`;
  }
  
  return true;
};